import type { GuestBusinessDetailsRecord } from "@/api/guest-business-details";
import { AddBusinessDetailsPrompt } from "@/components/AddBusinessDetailsPrompt";
import { SettingsContainer } from "@/components/ui/SettingsContainer";
import { Button, buttonVariants } from "@/components/ui/button/button";
import { COUNTRIES } from "@/constants/countries";
import type { States } from "@/features/states/schema";
import { cn } from "@/lib/utils";
import { formatEinForDisplay } from "@/pages/Home/setting/business-details/BusinessDetailsForm.utils";
import { Link, useLoaderData, useRouter } from "@tanstack/react-router";
import { ArrowLeftIcon, Pencil, X } from "lucide-react";

function formatPhoneLine(record: GuestBusinessDetailsRecord): string {
	const country = Object.values(COUNTRIES).find(
		(c) => c.id === String(record.business_phone_global_country_id),
	);
	const dial = country?.secondary?.replace(/\D/g, "") ?? "";
	const digits = record.business_phone.replace(/\D/g, "");
	if (!digits) return "";
	const spaced = dial ? `+${dial} ${digits}` : digits;
	return spaced;
}

function BusinessSummaryCard({
	record,
	states,
}: {
	record: GuestBusinessDetailsRecord;
	states: States;
}) {
	const stateName =
		states.states.find((s) => s.id === record.global_state_id)?.name ?? "";

	const ein = formatEinForDisplay(record.ein_number);

	const lines = [
		ein ? `EIN: ${ein}` : null,
		record.address,
		record.address_line_2,
		[record.city, stateName, record.zipcode].filter(Boolean).join(", "),
		formatPhoneLine(record),
	].filter(Boolean);

	return (
		<div
			className={cn(
				"w-full rounded-2xl border border-border p-4 text-left",
				"flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between",
			)}
		>
			<div className="min-w-0 space-y-1">
				<p className="font-semibold text-textDark text-base">
					{record.business_name}
				</p>
				{lines.map((line, i) => (
					<p
						key={`${record.id}-${i}`}
						className="text-sm text-textLight break-words"
					>
						{line}
					</p>
				))}
			</div>
			<Link
				className={cn(
					buttonVariants({ variant: "outline", size: "sm" }),
					"shrink-0 w-full sm:w-auto",
				)}
				params={{ businessDetailId: String(record.id) }}
				to="/main/setting/business-details/edit/$businessDetailId"
			>
				<Pencil className="size-4 mr-2" />
				Edit
			</Link>
		</div>
	);
}

const BusinessDetailsIndexPage = () => {
	const router = useRouter();
	const { guestBusinessDetails: records, states } = useLoaderData({
		from: "/main/setting/business-details",
	});

	return (
		<SettingsContainer className="flex flex-col max-w-screen-sm bg-white text-textDark pb-10">
			<div className="mb-4 flex justify-end md:justify-start">
				<Button
					variant="ghost"
					size="compact"
					className="self-start"
					type="button"
					onClick={() => router.navigate({ to: "/main/setting" })}
				>
					<X className="w-6 h-6 text-textDark md:hidden" />
					<ArrowLeftIcon className="w-6 h-6 text-textDark hidden md:block" />
				</Button>
			</div>

			{records.length === 0 ? (
				<AddBusinessDetailsPrompt embedInParent>
					<Button className="w-full" asChild>
						<Link to="/main/setting/business-details/create">
							Add business details
						</Link>
					</Button>
				</AddBusinessDetailsPrompt>
			) : (
				<div className="space-y-4">
					{records.map((record) => (
						<BusinessSummaryCard
							key={record.id}
							record={record}
							states={states}
						/>
					))}
				</div>
			)}
		</SettingsContainer>
	);
};

export default BusinessDetailsIndexPage;
